// external libraries
import { eachMonthOfInterval } from 'date-fns';

// constants
import { OPEN_REQUESTS_ADDR } from 'lib/EventBusUpdated';
import {
  EMPLOYEES_QUERY_KEY,
  OPEN_REQUESTS_QUERY_KEY
} from '../RosterPlan.consts';

// types
import type { Interval } from 'components/types';
import type { RosterLiveUpdateSubscription } from '../RosterPlan.types';

// utils
import { createHoursMessageFilter } from './createHoursMessageFilter';

/**
 * The addresses the screen listens on, and the query each one refreshes.
 *
 * Hours are published per calendar month (`{year}/{month}`, month 1-based), as
 * `EmployeesGridFeature.getPeriodAddress` subscribes to them. A duty change elsewhere reaches the
 * screen this way too: saving a duty line queues an hours recalculation
 * (`UtilsDelegate.triggerCalculations`), which publishes here — unless the work agreement has no
 * benefit that year, for which `HoursManagementService.calculateSynchronous` publishes nothing. Dojo's interval is always
 * one month, but a week or four weeks here can span two, so every month the interval touches gets
 * its own address. A recalculation publishes on every month of its year, not just the changed one,
 * so hours messages only count for an employee in the loaded response (`createHoursMessageFilter`).
 * Open requests have one tenant-wide address (`RequestsGridFeature._listenToEventbusNotifications`).
 * Their message bodies differ per type, so every one of them counts.
 *
 * Dojo's duty time regulation address is left out on purpose, although #2499 names it. When a
 * selected cost centre has `dtr.calculation.enabled`, every employees GET queues a DTR run for the
 * employees it returns (`calculateDtr` in `RosterEmployeesBuilderGroovyBase`), which publishes one
 * message per employee on that address, so refetching on it would refetch forever. It would not show anything either: the violations exist
 * only in the message, and Dojo patched them into its grid. They belong to the feature that renders
 * them, which has to read the message body instead of refetching.
 */
export const getRosterLiveUpdateSubscriptions = ({
  startDate,
  endDate
}: Interval): RosterLiveUpdateSubscription[] => {
  const hoursSubscriptions = eachMonthOfInterval({
    start: startDate,
    end: endDate
  }).map((month) => ({
    address: `/roster/hours/${month.getFullYear()}/${month.getMonth() + 1}`,
    queryKey: EMPLOYEES_QUERY_KEY,
    createFilter: createHoursMessageFilter
  }));

  return [
    ...hoursSubscriptions,
    { address: OPEN_REQUESTS_ADDR, queryKey: OPEN_REQUESTS_QUERY_KEY }
  ];
};
